import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Tag, Pencil, Check, X } from 'lucide-react'
import { categoriesApi } from '@/lib/api'

export default function CategoriesPage() {
  const [newName, setNewName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const [editError, setEditError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['form-categories'],
    queryFn: () => categoriesApi.list(),
    staleTime: 30_000,
  })

  const categories = data?.data ?? []

  const createMut = useMutation({
    mutationFn: (name: string) => categoriesApi.create(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-categories'] })
      setNewName('')
      setError(null)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.detail ?? 'Erro ao criar categoria'
      setError(msg === 'Category already exists' ? 'Essa categoria já existe.' : msg)
    },
  })

  const renameMut = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => categoriesApi.rename(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-categories'] })
      setEditingId(null)
      setEditError(null)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.detail ?? 'Erro ao renomear'
      setEditError(msg === 'Category already exists' ? 'Essa categoria já existe.' : msg)
    },
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => categoriesApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['form-categories'] }),
  })

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    const name = newName.trim()
    if (!name) return
    createMut.mutate(name)
  }

  function startEdit(cat: { id: number; name: string }) {
    setEditingId(cat.id)
    setEditingName(cat.name)
    setEditError(null)
  }

  function cancelEdit() {
    setEditingId(null)
    setEditError(null)
  }

  function commitEdit(id: number) {
    const name = editingName.trim()
    if (!name) return
    renameMut.mutate({ id, name })
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categorias</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gerir categorias disponíveis nos formulários
          </p>
        </div>
      </div>

      {/* New category form */}
      <div className="mt-6 card p-5">
        <h2 className="mb-3 text-sm font-semibold text-gray-700">Nova categoria</h2>
        <form onSubmit={handleCreate} className="flex gap-3">
          <input
            className="input flex-1"
            placeholder="Nome da categoria (ex: Manutenção)"
            value={newName}
            onChange={e => { setNewName(e.target.value); setError(null) }}
            maxLength={100}
          />
          <button
            type="submit"
            className="btn btn-primary flex items-center gap-2"
            disabled={createMut.isPending || !newName.trim()}
          >
            <Plus className="h-4 w-4" />
            {createMut.isPending ? 'A criar...' : 'Criar'}
          </button>
        </form>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>

      {/* List */}
      <div className="mt-6 card overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : categories.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <Tag className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p>Sem categorias. Cria a primeira acima.</p>
          </div>
        ) : (
          <table className="table min-w-full">
            <thead>
              <tr>
                <th>Nome</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat: { id: number; name: string; created_at: string }) => (
                <tr key={cat.id}>
                  <td>
                    {editingId === cat.id ? (
                      <div className="flex flex-col gap-1">
                        <input
                          className="input text-sm w-full max-w-xs"
                          value={editingName}
                          autoFocus
                          maxLength={100}
                          onChange={e => setEditingName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') commitEdit(cat.id)
                            if (e.key === 'Escape') cancelEdit()
                          }}
                        />
                        {editError && <p className="text-xs text-red-600">{editError}</p>}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4 text-primary-500 shrink-0" />
                        <span className="font-medium text-gray-900">{cat.name}</span>
                      </div>
                    )}
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {editingId === cat.id ? (
                        <>
                          <button
                            className="btn btn-ghost p-1 text-green-600 hover:bg-green-50"
                            title="Guardar"
                            onClick={() => commitEdit(cat.id)}
                            disabled={renameMut.isPending || !editingName.trim()}
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            className="btn btn-ghost p-1 text-gray-500 hover:bg-gray-100"
                            title="Cancelar"
                            onClick={cancelEdit}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn-ghost p-1 text-blue-500 hover:bg-blue-50"
                            title="Renomear"
                            onClick={() => startEdit(cat)}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            className="btn btn-ghost p-1 text-red-500 hover:bg-red-50"
                            title="Eliminar"
                            onClick={() => {
                              if (confirm(`Eliminar a categoria "${cat.name}"?`)) {
                                deleteMut.mutate(cat.id)
                              }
                            }}
                            disabled={deleteMut.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
